import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import { isTokenExpired, refreshToken } from "../services/authService";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, logout, loading } = useAuth(); // Include loading state from AuthContext
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    const checkToken = async () => {
      const accessToken = localStorage.getItem("access_token");
      const refreshTokenValue = localStorage.getItem("refresh_token");

      if (!isLoggedIn || !accessToken) {
        setIsAuthorized(false); // User is not logged in or no token found
        return;
      }

      if (isTokenExpired(accessToken)) {
        if (!refreshTokenValue) {
          setIsAuthorized(false); // No refresh token, redirect to login
          return;
        }

        try {
          // Attempt to refresh the token
          const newAccessToken = await refreshToken(refreshTokenValue);
          localStorage.setItem("access_token", newAccessToken);
          setIsAuthorized(true); // Token refreshed, allow access
        } catch (error) {
          console.error("Failed to refresh token:", error);
          logout(); // Log the user out if token refresh fails
          setIsAuthorized(false);
        }
      } else {
        setIsAuthorized(true); // Token is valid, allow access
      }
    };

    if (!loading) {
      // Only check the token after AuthContext has finished loading
      checkToken();
    }
  }, [isLoggedIn, logout, loading]);

  if (loading || isAuthorized === null) {
    // While checking the token or waiting for AuthContext to finish loading, show a loading state
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthorized) {
    // If not authorized, redirect to login
    return <Navigate to="/login" state={{ message: "You must be logged in to access this page." }} />;
  }

  // If authorized, render the child component
  return <>{children}</>;
};

export default ProtectedRoute;